import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './App.css';
import logo from './logo.png';
import {
	MdInsights,
	MdUpload,
	MdEventNote,
	MdCheckCircle,
	MdBarChart,
	MdStorage,
	MdLock,
	MdFavorite,
	MdArrowForward,
} from 'react-icons/md';

/* Reusable CTA button */
function CtaButton({ href, configError, disabled }) {
	return (
		<a
			href={href}
			className="primary-cta cta-btn"
			target="_blank"
			rel="noopener noreferrer"
			aria-disabled={disabled ? 'true' : 'false'}
			style={{ opacity: disabled ? 0.6 : 1 }}
		>
			{configError ? 'Config laden mislukt' : 'Start nu'}
		</a>
	);
}

/* Feature group data */
const featureGroups = [
	{
		title: 'Mestbalans',
		icon: <MdInsights size={32} color="#1B5E20" />,
		items: [
			'Direct inzicht in uw globale mestbalans',
			'Eenvoudig uploaden van uw prognose van de Mestbank',
			'Overzicht van uw begin- en eindvoorraad',
		],
	},
	{
		title: 'Percelen & Normen',
		icon: <MdEventNote size={32} color="#1B5E20" />,
		items: [
			'Bemestingsplan opstellen voor al uw percelen',
			'Controleer of u binnen de wettelijke normen blijft',
			'Perceelsfiches met bemesting, bespuiting en veldacties',
		],
	},
	{
		title: 'Rapportage & Simulatie',
		icon: <MdBarChart size={32} color="#1B5E20" />,
		items: [
			'Genereer en print een overzichtelijk rapport',
			'Automatische berekening van mestproductie',
			'Analyseer de balans van mestaanvoer en -afvoer',
		],
	},
];

/* Screenshot carousel data */
const screenshots = [
	{ img: '/img/Percelen.png', label: 'Percelen' },
	{ img: '/img/Perceel-details.png', label: 'Perceel Details' },
	{ img: '/img/Dieren.png', label: 'Dieren' },
	{ img: '/img/Mestbalans.png', label: 'Mestbalans' },
];

function HomePage() {
	const [config, setConfig] = useState(null);
	const [configError, setConfigError] = useState(null);
	const [activeSlide, setActiveSlide] = useState(0);
	const [paused, setPaused] = useState(false);
	const [lightbox, setLightbox] = useState(null);
	const animRefs = useRef([]);
	const observerRef = useRef(null);

	useEffect(() => {
		observerRef.current = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						entry.target.classList.add('visible');
					}
				});
			},
			{
				threshold: 0.1,
				rootMargin: '0px 0px -50px 0px',
			}
		);

		animRefs.current.forEach((ref) => {
			if (ref) {
				observerRef.current.observe(ref);
			}
		});

		return () => {
			if (observerRef.current) {
				observerRef.current.disconnect();
			}
		};
	}, []);

	useEffect(() => {
		fetch('/config.json')
			.then((res) => {
				if (!res.ok) throw new Error('Config not found');
				return res.json();
			})
			.then(setConfig)
			.catch((err) => setConfigError(err.message));
	}, []);

	/* Auto-rotate carousel */
	useEffect(() => {
		if (paused) return;
		const timer = setInterval(() => {
			setActiveSlide((prev) => (prev + 1) % screenshots.length);
		}, 5000);
		return () => clearInterval(timer);
	}, [paused]);

	const appLink = config ? config.APP_LINK : '#';
	const ctaDisabled = !config;
	const addRef = (el) => {
		if (el && !animRefs.current.includes(el)) {
			animRefs.current.push(el);
			if (observerRef.current) {
				observerRef.current.observe(el);
			}
		}
	};

	return (
		<div className="App">
			{/* ─── HERO ─── */}
			<header className="app-header">
				<div className="header-inner">
					<img src={logo} alt="mestprogramma logo" className="logo" />

					<div className="header-text">
						<h1>Uw mestbalans onder controle</h1>
						<p className="subtitle">
							Stel een bemestingsplan op en beheer uw mestbalans
							in een moderne webapp, eenvoudig en overzichtelijk.
						</p>
						<p className="context-line">
							Afgestemd op het MAP en uw normen van de Mestbank.
						</p>
					</div>

					<div className="cta-wrap">
						<CtaButton href={appLink} configError={configError} disabled={ctaDisabled} />
						<div className="cta-subtext">
							Binnen enkele minuten aan de slag
						</div>
					</div>
				</div>
			</header>

			{/* ─── HOE HET WERKT ─── */}
			<section className="how-it-works-section" ref={addRef}>
				<h2>Hoe het werkt</h2>
				<div className="steps-row">
					<div className="step" ref={addRef}>
						<div className="step-number">1</div>
						<div className="step-icon">
							<MdUpload size={36} color="#1B5E20" />
						</div>
						<h3>Upload uw prognosebestand</h3>
						<p>
							Download uw prognosebestand van de Mestbank en upload het. Uw
							percelen, teelten en normen worden automatisch ingeladen.
						</p>
					</div>

					<div className="step-connector" aria-hidden="true">
						<MdArrowForward size={24} color="#4CAF50" />
					</div>

					<div className="step" ref={addRef}>
						<div className="step-number">2</div>
						<div className="step-icon">
							<MdStorage size={36} color="#1B5E20" />
						</div>
						<h3>Voeg uw dieren en mest toe</h3>
						<p>
							Vul uw veebezetting aan en bepaal per perceel welke mest u
							toedient. Het programma berekent automatisch uw mestproductie
							en gebruik.
						</p>
					</div>

					<div className="step-connector" aria-hidden="true">
						<MdArrowForward size={24} color="#4CAF50" />
					</div>

					<div className="step" ref={addRef}>
						<div className="step-number">3</div>
						<div className="step-icon">
							<MdInsights size={36} color="#1B5E20" />
						</div>
						<h3>Bekijk uw mestbalans</h3>
						<p>
							Zie in één oogopslag of u binnen de normen blijft. Plan bij
							het begin van het jaar welke bemesting u wanneer uitvoert en
							genereer rapporten.
						</p>
					</div>
				</div>
			</section>

			{/* ─── SCREENSHOTS CAROUSEL ─── */}
			<section className="screenshots-section" ref={addRef}>
				<h2>Bekijk het mestprogramma</h2>
				<div
					className="screenshots-carousel"
					onMouseEnter={() => setPaused(true)}
					onMouseLeave={() => setPaused(false)}
				>
					<button
						className="carousel-arrow carousel-arrow-left"
						onClick={() => setActiveSlide((prev) => (prev - 1 + screenshots.length) % screenshots.length)}
						aria-label="Vorige screenshot"
					>
						&#8249;
					</button>

					<div className="carousel-slides">
						{screenshots.map((shot, i) => (
							<div
								className={`carousel-slide${i === activeSlide ? ' active' : ''}`}
								key={i}
							>
								<div className="browser-chrome">
									<div className="browser-bar">
										<span className="browser-dot" />
										<span className="browser-dot" />
										<span className="browser-dot" />
										<span className="browser-address">mestprogramma.be</span>
									</div>
									<img
									src={shot.img}
									alt={shot.label}
									className="carousel-slide-img lightbox-trigger"
									onClick={() => setLightbox({ src: shot.img, alt: shot.label })}
								/>
								</div>
								<span className="carousel-slide-label">{shot.label}</span>
							</div>
						))}
					</div>

					<button
						className="carousel-arrow carousel-arrow-right"
						onClick={() => setActiveSlide((prev) => (prev + 1) % screenshots.length)}
						aria-label="Volgende screenshot"
					>
						&#8250;
					</button>
				</div>

				<div className="carousel-dots">
					{screenshots.map((_, i) => (
						<button
							key={i}
							className={`carousel-dot${i === activeSlide ? ' active' : ''}`}
							onClick={() => setActiveSlide(i)}
							aria-label={`Ga naar screenshot ${i + 1}`}
						/>
					))}
				</div>
			</section>

			{/* ─── SIMULATOR HIGHLIGHT ─── */}
			<section className="simulator-section" ref={addRef}>
				<div className="simulator-inner">
					<div className="simulator-text">
						<h2>Snel simuleren</h2>
						<p>
							Geen volledig bemestingsplan nodig om uw mestbalans te berekenen.
							Met de simulator vertrekt u vanuit uw huidige cijfers en ziet
							u meteen het effect van mestaanvoer of -afvoer op uw
							mestbalans. Ideaal om snel scenario's te checken.
						</p>
						<a
							href={appLink}
							className="secondary-cta-btn"
							target="_blank"
							rel="noopener noreferrer"
							style={{ opacity: ctaDisabled ? 0.6 : 1 }}
						>
							Probeer de simulator
						</a>
					</div>
					<div className="simulator-visual">
						<img
						src="/img/Simulator.png"
						alt="Simulator"
						className="simulator-img lightbox-trigger"
						onClick={() => setLightbox({ src: '/img/Simulator.png', alt: 'Simulator' })}
					/>
					</div>
				</div>
			</section>

			{/* ─── PERCEELSFICHES ─── */}
			<section className="simulator-section perceelsfiche-section" ref={addRef}>
				<div className="simulator-inner simulator-inner--reversed">
					<div className="simulator-visual">
						<img
							src="/img/veldwerk.png"
							alt="Perceelsfiche met bemesting, bespuiting en veldacties"
							className="simulator-img lightbox-trigger"
							onClick={() => setLightbox({ src: '/img/veldwerk.png', alt: 'Perceelsfiche' })}
						/>
					</div>
					<div className="simulator-text">
						<h2>Perceelsfiches</h2>
						<p>
							Voor elk perceel genereert het mestprogramma een gedetailleerde
							perceelsfiche. Bekijk in één overzicht alle bemesting, bespuiting en veldacties.
							Ideaal als naslagwerk en voor uw bedrijfsadministratie.
						</p>
					</div>
				</div>
			</section>

			{/* ─── FEATURES (grouped) ─── */}
			<section className="features-section">
				<h2>Wat kan het mestprogramma voor u betekenen?</h2>
				<div className="feature-groups">
					{featureGroups.map((group, gi) => (
						<div
							key={gi}
							className="feature-group"
							ref={addRef}
						>
							<div className="feature-group-header">
								{group.icon}
								<h3>{group.title}</h3>
							</div>
							<ul className="feature-group-list">
								{group.items.map((item, ii) => (
									<li key={ii}>
										<MdCheckCircle size={18} color="#4CAF50" />
										<span>{item}</span>
									</li>
								))}
							</ul>
						</div>
					))}
				</div>
			</section>

			{/* ─── TRUST BADGES ─── */}
			<section className="trust-section" ref={addRef}>
				<div className="trust-badges">
					<div className="trust-badge" ref={addRef}>
						<MdCheckCircle size={32} color="#1B5E20" />
						<strong>Conform VLM richtlijnen</strong>
						<span>Afgestemd op de normen van de Mestbank</span>
					</div>
					<div className="trust-badge" ref={addRef}>
						<MdLock size={32} color="#1B5E20" />
						<strong>GDPR-conform</strong>
						<span>Gehost in de EU &middot; Uw data blijft veilig</span>
					</div>
					<div className="trust-badge" ref={addRef}>
						<MdFavorite size={32} color="#1B5E20" />
						<strong>Gemaakt in Vlaanderen</strong>
						<span>Ontwikkeld met kennis van de Vlaamse landbouw</span>
					</div>
				</div>
				<p className="trust-privacy">
					Uw gegevens worden nooit gedeeld met derden.{' '}
					<Link to="/privacybeleid">Lees ons privacybeleid</Link>.
				</p>
			</section>

			{/* ─── BOTTOM CTA ─── */}
			<section className="bottom-cta-section" ref={addRef}>
				<h2>Klaar om te starten?</h2>
				<p>
					Ontdek hoe eenvoudig het is om uw mestbalans te beheren.
				</p>
				<CtaButton href={appLink} configError={configError} disabled={ctaDisabled} />
				<div className="cta-subtext">Binnen enkele minuten aan de slag</div>
			</section>

			{/* ─── FOOTER ─── */}
			<footer className="footer">
				<div className="footer-inner">
					<div className="footer-left">
						&copy; {new Date().getFullYear()} mestprogramma &middot; Gemaakt door DK
					</div>
					<div className="footer-right">
						<a href="mailto:info@mestprogramma.be" className="contact-email">
							info@mestprogramma.be
						</a>
						<span className="footer-separator">&middot;</span>
						<Link to="/privacybeleid" className="footer-link">
							Privacybeleid
						</Link>
						<span className="footer-separator">&middot;</span>
						<Link to="/gebruikersvoorwaarden" className="footer-link">
							Gebruikersvoorwaarden
						</Link>
					</div>
				</div>
				<div className="footer-seo">
					<p>
						Mestprogramma is een online tool voor Vlaamse landbouwers om hun
						globale mestbalans te berekenen, percelen te beheren en te voldoen
						aan het mestdecreet en de MAP-normen. Upload uw prognosebestand van
						de Mestbank, stel een bemestingsplan (ook wel bemestingplan) op en
						genereer perceelsfiches met bemesting, bespuiting en veldacties per
						perceel. Alles in één overzichtelijke webapp.
					</p>
				</div>
			</footer>
			{/* ─── LIGHTBOX ─── */}
			{lightbox && (
				<div className="lightbox-overlay" onClick={() => setLightbox(null)}>
					<button
						className="lightbox-close"
						onClick={() => setLightbox(null)}
						aria-label="Sluiten"
					>
						&times;
					</button>
					<img
						src={lightbox.src}
						alt={lightbox.alt}
						className="lightbox-img"
						onClick={() => setLightbox(null)}
					/>
				</div>
			)}
		</div>
	);
}

export default HomePage;
